import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { DOCTORS } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, MagnifyingGlass, PencilSimple, Trash, MapPin } from '@phosphor-icons/react';

export default function DoctorsPage() {
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.getDoctors(search); setDoctors(data); } catch {}
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid={DOCTORS.container} className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Doctors</h1>
          <p className="text-sm mt-1" style={{ color: '#8A94A6' }}>{doctors.length} registered doctors</p>
        </div>
        <Button data-testid={DOCTORS.addDoctorBtn} onClick={() => { setEditDoc(null); setShowForm(true); }}
          className="h-9 px-4 text-sm font-semibold rounded-md" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} weight="bold" className="mr-1" /> Add Doctor
        </Button>
      </div>

      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
        <Input data-testid={DOCTORS.searchInput} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search doctors..." className="pl-10 h-9" />
      </div>

      <div data-testid={DOCTORS.doctorTable} className="rounded-md border overflow-x-auto" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <thead>
            <tr style={{ background: '#F9F9FA', borderBottom: '1px solid #E2E4E9' }}>
              {['Name', 'Specialization', 'Phone', 'Clinic Address', 'Area', 'Commission %', 'Actions'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr>
            ) : doctors.map(d => (
              <tr key={d.id} className="hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #E2E4E9' }}>
                <td className="px-3 py-2 font-medium" style={{ color: '#0A2540' }}>{d.name}</td>
                <td className="px-3 py-2 text-xs" style={{ color: '#8A94A6' }}>{d.specialization}</td>
                <td className="px-3 py-2 text-xs">{d.phone}</td>
                <td className="px-3 py-2 text-xs flex items-center gap-1">
                  {d.latitude && <MapPin size={12} style={{ color: '#007AFF' }} />}
                  {d.clinic_address}
                </td>
                <td className="px-3 py-2 text-xs">{d.area}</td>
                <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.commission_percent}%</td>
                <td className="px-3 py-2 flex gap-1">
                  <button onClick={() => { setEditDoc(d); setShowForm(true); }} className="p-1 rounded hover:bg-slate-100"><PencilSimple size={14} style={{ color: '#007AFF' }} /></button>
                  <button onClick={async () => { if (window.confirm('Delete?')) { await api.deleteDoctor(d.id); load(); } }} className="p-1 rounded hover:bg-red-50"><Trash size={14} style={{ color: '#FF3B30' }} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editDoc ? 'Edit Doctor' : 'Add Doctor'}</DialogTitle></DialogHeader>
          <DoctorForm doctor={editDoc} onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DoctorForm({ doctor, onSave }) {
  const [form, setForm] = useState(doctor || { name: '', specialization: '', phone: '', clinic_address: '', area: '', latitude: null, longitude: null, commission_percent: 0 });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      if (doctor?.id) await api.updateDoctor(doctor.id, form);
      else await api.createDoctor(form);
      onSave();
    } catch {}
  };
  return (
    <div className="space-y-3">
      {[
        { k: 'name', label: 'Name' }, { k: 'specialization', label: 'Specialization' },
        { k: 'phone', label: 'Phone' }, { k: 'clinic_address', label: 'Clinic Address' },
        { k: 'area', label: 'Area' },
      ].map(f => (
        <div key={f.k}>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{f.label}</Label>
          <Input value={form[f.k] || ''} onChange={e => update(f.k, e.target.value)} className="h-8 mt-1 text-sm" />
        </div>
      ))}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Latitude</Label>
          <Input type="number" step="any" value={form.latitude || ''} onChange={e => update('latitude', Number(e.target.value) || null)} className="h-8 mt-1 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Longitude</Label>
          <Input type="number" step="any" value={form.longitude || ''} onChange={e => update('longitude', Number(e.target.value) || null)} className="h-8 mt-1 text-sm" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Commission %</Label>
        <Input type="number" value={form.commission_percent} onChange={e => update('commission_percent', Number(e.target.value))} className="h-8 mt-1 text-sm" />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button onClick={save} className="h-8 text-sm" style={{ background: '#0A2540', color: 'white' }}>Save</Button>
      </div>
    </div>
  );
}
